import type { Integration, IntegrationCapabilities } from '@hostesa/api-client';
import { useDisableIntegration, useTestIntegration } from '@hostesa/api-client';
import { useFormatters, useT } from '@hostesa/i18n';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  StatusBadge,
  toast,
} from '@hostesa/ui';
import { useState } from 'react';
import { ConnectionStatus } from './connection-status';
import { namespace } from './i18n';

export interface IntegrationCardProps {
  tenantId: string;
  timezone: string;
  integration: Integration;
  capabilities?: IntegrationCapabilities;
}

/** Render the capability matrix as a flat list of capability keys. */
function capabilityKeys(matrix: Record<string, unknown> | undefined): string[] {
  if (!matrix) return [];
  return Object.entries(matrix)
    .filter(([, enabled]) => enabled !== false && enabled !== null && enabled !== undefined)
    .map(([key]) => key);
}

/**
 * Existing-integration card: connection status, capabilities, and the
 * test/disable actions.
 *
 * - Test is NON-OPTIMISTIC: the button shows a pending label while the probe is
 *   in-flight and the result (success/failure) is shown only after the server
 *   responds — we never optimistically flip the status.
 * - Disable is confirmed via a dialog before the (non-optimistic) mutation.
 */
export function IntegrationCard({
  tenantId,
  timezone,
  integration,
  capabilities,
}: IntegrationCardProps) {
  const { t } = useT(namespace);
  const f = useFormatters(timezone);

  const test = useTestIntegration(tenantId);
  const disable = useDisableIntegration(tenantId);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const caps =
    capabilityKeys(capabilities?.capabilities as Record<string, unknown> | undefined).length > 0
      ? capabilityKeys(capabilities?.capabilities as Record<string, unknown> | undefined)
      : capabilityKeys(integration.capabilityMatrix);

  const onTest = () => {
    test.mutate(
      { integrationId: integration.id },
      {
        onSuccess: (result) => {
          if (result.success) toast.success(t('card.testSuccess'));
          else toast.error(t('card.testFailure'));
        },
        onError: () => toast.error(t('card.testFailure')),
      },
    );
  };

  const onConfirmDisable = () => {
    disable.mutate(
      { integrationId: integration.id },
      {
        onSuccess: () => {
          toast.success(t('card.disableSuccess'));
          setConfirmOpen(false);
        },
        onError: () => toast.error(t('card.disableError')),
      },
    );
  };

  const lastTested =
    typeof integration.lastTestedAt === 'string'
      ? f.formatDateTime(integration.lastTestedAt)
      : t('card.neverTested');

  return (
    <Card data-testid="integration-card">
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <CardTitle data-testid="integration-card-provider">
            {t('card.providerLabel')}: {integration.provider}
          </CardTitle>
          <ConnectionStatus status={integration.status} data-testid="integration-card-status" />
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex items-center gap-2 text-sm text-[var(--color-stone-mid)]">
          <span>{t('card.lastTestedLabel')}:</span>
          <span data-testid="integration-card-last-tested">{lastTested}</span>
        </div>

        <div className="flex flex-col gap-2">
          <p className="text-xs font-medium uppercase tracking-wide text-[var(--color-stone-mid)]">
            {t('card.capabilitiesTitle')}
          </p>
          {caps.length === 0 ? (
            <p
              data-testid="integration-card-no-capabilities"
              className="text-sm text-[var(--color-stone-mid)]"
            >
              {t('card.noCapabilities')}
            </p>
          ) : (
            <ul data-testid="integration-card-capabilities" className="flex flex-wrap gap-1.5">
              {caps.map((cap) => (
                <li key={cap}>
                  <StatusBadge
                    status="neutral"
                    label={cap}
                    data-testid={`integration-capability-${cap}`}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="secondary"
            data-testid="integration-test-btn"
            onClick={onTest}
            disabled={test.isPending}
          >
            {test.isPending ? t('card.testing') : t('card.testBtn')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            data-testid="integration-disable-btn"
            onClick={() => setConfirmOpen(true)}
            disabled={integration.status === 'disabled' || disable.isPending}
          >
            {t('card.disableBtn')}
          </Button>
        </div>
      </CardContent>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="integration-disable-confirm-modal">
          <DialogHeader>
            <DialogTitle>{t('confirmDisable.title')}</DialogTitle>
            <DialogDescription>{t('confirmDisable.description')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              data-testid="integration-disable-cancel-btn"
              onClick={() => setConfirmOpen(false)}
            >
              {t('confirmDisable.cancelBtn')}
            </Button>
            <Button
              type="button"
              variant="destructive"
              data-testid="integration-disable-confirm-btn"
              onClick={onConfirmDisable}
              disabled={disable.isPending}
            >
              {disable.isPending ? t('card.disabling') : t('confirmDisable.confirmBtn')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
